import React from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';

interface PageHeaderProps {
  icon?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: string;
  actions?: React.ReactNode;
  /** Back button configuration */
  backButton?: {
    label?: string;
    onClick?: () => void;
    icon?: React.ReactNode;
  };
  /** Loading state */
  loading?: boolean;
}

/**
 * Responsive page header used across all list and report pages.
 * On desktop (md+): title left, actions right.
 * On mobile (xs/sm): title stacks above a wrapping actions row.
 */
const PageHeader: React.FC<PageHeaderProps> = ({ 
  icon, 
  title, 
  subtitle, 
  actions,
  backButton,
  loading = false,
}) => {
  return (
    <Box
      sx={{
        mb: 3,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: { xs: 'flex-start', md: 'space-between' },
        alignItems: 'flex-start',
        gap: { xs: 1.5, md: 0 },
        opacity: loading ? 0.6 : 1,
      }}
    >
      {/* Back button + Title + subtitle */}
      <Box sx={{ minWidth: 0, flex: { xs: 'unset', md: '1' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: backButton ? 0.5 : 0 }}>
          {backButton && (
            <Button
              size="small"
              startIcon={backButton.icon || <ArrowBackIcon fontSize="small" />}
              onClick={backButton.onClick}
              sx={{ mr: 0.5 }}
            >
              {backButton.label || 'กลับ'}
            </Button>
          )}
        </Box>
        <Typography
          variant="h5"
          component="h1"
          gutterBottom={!backButton}
          sx={{
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            lineHeight: 1.3,
          }}
        >
          {icon}
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>

      {/* Actions row */}
      {actions && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            flexShrink: 0,
            flexWrap: 'wrap',
          }}
        >
          {actions}
        </Box>
      )}
    </Box>
  );
};

export default PageHeader;
